import axios from "axios";
import { getToken } from "Shared/baseURL";
import { attachApiInterceptors } from "Shared/apiInterceptors";
import { sortSubjectsByName } from "Utils/sortSubjects";
import { getListCreatedTimestamp } from "Utils/listCreatedTime";
import {
  assertDefinitionDoesNotRevealKeyword,
  doesDefinitionRevealKeyword,
} from "Utils/keywordDefinitionSafety";
import { assertNoProfanityInFields } from "Utils/profanityGuard";

const resolveKeywordServiceBase = () => {
  if (import.meta.env.VITE_KEYWORD_SERVICE_BASE) {
    return import.meta.env.VITE_KEYWORD_SERVICE_BASE;
  }

  const base = import.meta.env.VITE_BASEURL || "";
  const normalizedBase = String(base).replace(/\/$/, "");
  return normalizedBase ? `${normalizedBase}/ks/api` : "/api/ks/api";
};

const KEYWORD_SERVICE_BASE = resolveKeywordServiceBase().replace(/\/$/, "");

const keywordServiceClient = axios.create({
  baseURL: KEYWORD_SERVICE_BASE,
});
attachApiInterceptors(keywordServiceClient, "keywordServiceClient");
const AI_DEFINITION_GENERATION_ATTEMPTS = 3;
const MAX_AI_GENERATED_KEYWORD_CANDIDATES = 35;

const buildKeywordServicePath = (path = "") => {
  if (typeof path !== "string") {
    return "";
  }
  return path.replace(/^\/+/, "");
};

const normalizeText = (value) => {
  if (value == null) return "";
  return String(value).trim();
};

const extractApiErrorMessage = (error) => {
  const data = error?.response?.data;

  if (typeof data === "string") {
    return normalizeText(data);
  }

  if (data && typeof data === "object") {
    return normalizeText(
      data.message || data.error || data.detail || data.title,
    );
  }

  return normalizeText(error?.message);
};

const normalizeStringList = (values) => {
  if (!Array.isArray(values)) {
    return [];
  }

  return values.map((item) => normalizeText(item)).filter(Boolean);
};

const getKeywordDefinitionText = (payload) => {
  const firstDefinition = Array.isArray(payload?.definitions)
    ? payload.definitions[0] || {}
    : {};
  const firstQuestion = Array.isArray(payload?.questions)
    ? payload.questions[0] || {}
    : {};

  return normalizeText(
    payload?.definition ||
      payload?.defination ||
      payload?.description ||
      firstDefinition?.definition ||
      firstDefinition?.defination ||
      firstQuestion?.definition ||
      firstQuestion?.defination,
  );
};

const isKeywordDefinitionSafe = (keyword, definition) => {
  const normalizedKeyword = normalizeText(keyword);
  const normalizedDefinition = normalizeText(definition);
  return (
    !normalizedKeyword ||
    !normalizedDefinition ||
    !doesDefinitionRevealKeyword(normalizedKeyword, normalizedDefinition)
  );
};

const assertKeywordDefinitionIsSafe = (
  keyword,
  definition,
  source = "Definition",
) => {
  const normalizedKeyword = normalizeText(keyword);
  const normalizedDefinition = normalizeText(definition);
  if (!normalizedKeyword || !normalizedDefinition) {
    return;
  }

  assertDefinitionDoesNotRevealKeyword(
    normalizedKeyword,
    normalizedDefinition,
    { source },
  );
};

const buildUnsafeGeneratedDefinitionError = (keyword) =>
  new Error(
    `AI generated definitions for "${normalizeText(
      keyword,
    )}" kept revealing the answer. Please try again.`,
  );

const getKeywordQuestionText = (payload) => {
  const firstQuestion = Array.isArray(payload?.questions)
    ? payload.questions[0] || {}
    : {};

  return normalizeText(
    payload?.question ||
      payload?.prompt ||
      firstQuestion?.question ||
      firstQuestion?.prompt,
  );
};

const assertListTextIsClean = (payload = {}) =>
  assertNoProfanityInFields([
    {
      field: "title",
      label: "List title",
      value: payload?.title || payload?.name,
    },
  ]);

const getKeywordNameText = (payload = {}) =>
  normalizeText(
    (payload && typeof payload.get === "function" ? payload.get("name") : "") ||
    payload?.name ||
      payload?.keywordName ||
      payload?.keyword ||
      payload?.term,
  );

const assertKeywordPayloadTextIsClean = (payload = {}) =>
  assertNoProfanityInFields([
    { field: "name", label: "Keyword name", value: getKeywordNameText(payload) },
  ]);

const isRecord = (value) =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);

const clampInteger = (value, { min = 1, max = 20, fallback = 1 } = {}) => {
  const numeric = Number(value);
  const integer = Number.isFinite(numeric) ? Math.trunc(numeric) : fallback;
  return Math.min(max, Math.max(min, integer));
};

const normalizeGeneratedKeywordCandidate = (payload) => {
  if (!payload || typeof payload !== "object") {
    return {
      tempId: "",
      name: "",
      definition: "",
      question: "",
      context: "",
      distractors: [],
    };
  }

  return {
    ...payload,
    tempId: normalizeText(payload.tempId || payload.id || payload.uuid),
    name: normalizeText(
      payload.name ||
        payload.keyword ||
        payload.keywordName ||
        payload.term ||
        payload.title,
    ),
    definition: normalizeText(payload.definition || payload.defination),
    question: normalizeText(payload.question || payload.prompt),
    context: normalizeText(payload.context || payload.notesText || payload.note),
    distractors: normalizeStringList(payload.distractors),
  };
};

const extractGeneratedKeywordCandidates = (payload) => {
  const candidateSources = [
    payload,
    payload?.data,
    payload?.content,
    payload?.items,
    payload?.keywords,
    payload?.candidates,
    payload?.results,
    payload?.data?.content,
    payload?.data?.items,
    payload?.data?.keywords,
    payload?.data?.candidates,
    payload?.data?.results,
  ];

  const rawCandidates =
    candidateSources.find((source) => Array.isArray(source)) || [];

  return rawCandidates
    .map(normalizeGeneratedKeywordCandidate)
    .filter((candidate) =>
      isKeywordDefinitionSafe(candidate?.name, candidate?.definition),
    );
};

const pickGeneratedKeywordCandidatePayload = (payload) => {
  if (Array.isArray(payload)) {
    return payload[0] || {};
  }

  if (!isRecord(payload)) {
    return {};
  }

  const nestedCandidate = [
    payload.candidate,
    payload.keyword,
    payload.generatedKeyword,
    payload.generatedKeywordCandidate,
    payload.item,
    payload.result,
    payload.data,
  ].find(isRecord);

  if (nestedCandidate) {
    return pickGeneratedKeywordCandidatePayload(nestedCandidate);
  }

  return payload;
};

const normalizeSubjectRecord = (payload) => {
  if (!payload || typeof payload !== "object") {
    return {
      id: null,
      uuid: null,
      href: "",
      name: "",
    };
  }

  const rawId =
    payload.id ||
    payload.subjectId ||
    payload.uuid ||
    payload.href ||
    payload.reference ||
    null;

  const subjectId = coerceId(rawId);

  const derivedHref =
    payload.href ||
    payload.reference ||
    payload.slug ||
    (subjectId != null ? String(subjectId) : "");

  return {
    ...payload,
    id: subjectId,
    uuid: subjectId != null ? String(subjectId) : payload.uuid || null,
    href: derivedHref,
    name:
      payload.name ||
      payload.title ||
      payload.subjectName ||
      payload.displayName ||
      "",
  };
};

const isFiniteNumber = (value) => typeof value === "number" && Number.isFinite(value);

const coerceId = (value) => {
  if (typeof value === "string" && value.trim().length > 0) {
    const numeric = Number(value);
    return Number.isNaN(numeric) ? value : numeric;
  }
  if (isFiniteNumber(value)) {
    return value;
  }
  return null;
};

const normalizeNonNegativeCount = (...values) => {
  for (const value of values) {
    if (Array.isArray(value)) {
      return value.length;
    }

    const numeric = Number(value);
    if (Number.isFinite(numeric) && numeric >= 0) {
      return numeric;
    }
  }

  return 0;
};

const normalizeSubjectSummary = (payload) => {
  if (!payload || typeof payload !== "object") {
    return {
      id: null,
      uuid: null,
      href: "",
      name: "",
    };
  }

  const candidateId = coerceId(
    payload.subjectId || payload.id || payload.uuid || payload.href || payload.reference
  );

  const hrefCandidate =
    payload.href || payload.reference || payload.slug || (candidateId != null ? String(candidateId) : "");

  return {
    ...payload,
    id: candidateId,
    uuid: candidateId != null ? String(candidateId) : payload.uuid || null,
    href: hrefCandidate,
    name:
      payload.name ||
      payload.title ||
      payload.subjectName ||
      payload.displayName ||
      payload.subject ||
      "",
  };
};

const normalizeListRecord = (payload) => {
  if (!payload || typeof payload !== "object") {
    return {
      id: null,
      uuid: null,
      href: "",
      name: "",
      keywords: [],
      keywordsCount: 0,
      description: "",
      coverImageUrl: null,
      visibility: "PUBLIC",
      subject: normalizeSubjectSummary(null),
      createdAt: null,
      likes: 0,
      dislikes: 0,
      views: 0,
      cloneCount: 0,
    };
  }

  const rawId =
    payload.id ||
    payload.listId ||
    payload.uuid ||
    payload.href ||
    payload._links?.self?.href ||
    null;

  const listId = coerceId(rawId);
  const subjectSummary = normalizeSubjectSummary({
    subjectId: payload.subjectId,
    name: payload.subjectName,
    href: payload.subjectHref,
    ...payload.subject,
  });

  const derivedHref =
    payload.href ||
    payload.slug ||
    payload.reference ||
    (listId != null ? String(listId) : "");

  const keywordsArray = Array.isArray(payload.keywords)
    ? payload.keywords.map((keyword) => ({
        ...keyword,
        uuid: keyword.uuid || keyword.id || keyword.keywordId || null,
        term:
          keyword.term ||
          keyword.name ||
          keyword.keyword ||
          keyword.title ||
          "",
        definition: getKeywordDefinitionText(keyword),
      }))
    : [];

  const normalizedCoverImageMediaId =
    payload.coverImageMediaId ??
    payload.coverMediaId ??
    payload.coverImageId ??
    payload.mediaId ??
    null;
  const normalizedCoverImageUrl = payload.coverImageUrl || payload.coverImage || payload.imageUrl || null;


  return {
    ...payload,
    id: listId,
    uuid: listId != null ? String(listId) : payload.uuid || null,
    href: derivedHref,
    name: payload.name || payload.title || payload.listName || "",
    description: payload.description || payload.summary || "",
    coverImageMediaId: normalizedCoverImageMediaId,
    coverImageUrl: normalizedCoverImageUrl,
    visibility: payload.visibility || payload.privacy || "PUBLIC",
    subject: subjectSummary,
    keywords: keywordsArray,
    keywordsCount:
      typeof payload.keywordsCount === "number"
        ? payload.keywordsCount
        : keywordsArray.length,
    likes: typeof payload.likes === "number" ? payload.likes : payload.reactions?.likes || 0,
    dislikes:
      typeof payload.dislikes === "number" ? payload.dislikes : payload.reactions?.dislikes || 0,
    views: typeof payload.views === "number" ? payload.views : payload.viewCount || 0,
    cloneCount: normalizeNonNegativeCount(
      payload.cloneCount,
      payload.clonesCount,
      payload.clonedCount,
      payload.copyCount,
      payload.copiesCount,
      payload.clones,
      payload.clonedBy,
    ),
    createdAt: getListCreatedTimestamp(payload),
    pinned: Boolean(payload.pinned),
  };
};

const normalizePagePayload = (payload, normalizeItem) => {
  const pageContent = Array.isArray(payload?.content)
    ? payload.content
    : Array.isArray(payload?.items)
    ? payload.items
    : Array.isArray(payload?.lists)
    ? payload.lists
    : [];

  const pageNumber = isFiniteNumber(payload?.pageable?.pageNumber)
    ? payload.pageable.pageNumber
    : isFiniteNumber(payload?.page)
    ? payload.page
    : isFiniteNumber(payload?.pagination?.page)
    ? Math.max(0, payload.pagination.page - 1)
    : 0;

  const pageSize = isFiniteNumber(payload?.pageable?.pageSize)
    ? payload.pageable.pageSize
    : isFiniteNumber(payload?.size)
    ? payload.size
    : isFiniteNumber(payload?.pagination?.size)
    ? payload.pagination.size
    : pageContent.length;

  const totalElements = isFiniteNumber(payload?.totalElements)
    ? payload.totalElements
    : isFiniteNumber(payload?.pagination?.total)
    ? payload.pagination.total
    : pageContent.length;

  const totalPages = isFiniteNumber(payload?.totalPages)
    ? payload.totalPages
    : isFiniteNumber(payload?.pagination?.totalPages)
    ? payload.pagination.totalPages
    : pageSize > 0
    ? Math.ceil(totalElements / pageSize)
    : 0;

  return {
    list: pageContent.map(normalizeItem),
    lists: pageContent.map(normalizeItem),
    pagination: {
      page: pageNumber + 1,
      pageIndex: pageNumber,
      size: pageSize,
      total: totalElements,
      totalPages,
    },
  };
};

const normalizeSubjectPagePayload = (payload) => {
  const rawSubjects = Array.isArray(payload?.subjects)
    ? payload.subjects
    : Array.isArray(payload?.content)
    ? payload.content
    : Array.isArray(payload?.items)
    ? payload.items
    : [];

  const subjects = sortSubjectsByName(
    rawSubjects.map((subject) => normalizeSubjectRecord(subject))
  );

  const pageNumber = isFiniteNumber(payload?.pageable?.pageNumber)
    ? payload.pageable.pageNumber
    : isFiniteNumber(payload?.page)
    ? payload.page
    : isFiniteNumber(payload?.pagination?.page)
    ? Math.max(0, payload.pagination.page - 1)
    : 0;

  const pageSize = isFiniteNumber(payload?.pageable?.pageSize)
    ? payload.pageable.pageSize
    : isFiniteNumber(payload?.size)
    ? payload.size
    : isFiniteNumber(payload?.pagination?.size)
    ? payload.pagination.size
    : subjects.length;

  const totalElements = isFiniteNumber(payload?.totalElements)
    ? payload.totalElements
    : isFiniteNumber(payload?.pagination?.total)
    ? payload.pagination.total
    : subjects.length;

  const totalPages = isFiniteNumber(payload?.totalPages)
    ? payload.totalPages
    : isFiniteNumber(payload?.pagination?.totalPages)
    ? payload.pagination.totalPages
    : pageSize > 0
    ? Math.ceil(totalElements / pageSize)
    : 0;

  return {
    subjects,
    pagination: {
      page: pageNumber + 1,
      pageIndex: pageNumber,
      size: pageSize,
      total: totalElements,
      totalPages,
    },
  };
};

const withAuthHeaders = async (config = {}) => {
  const token = await getToken();
  return {
    ...config,
    headers: {
      ...(config.headers || {}),
      ...(token ? { Authorization: token } : {}),
    },
  };
};

const normalizeSortParam = (sortValue) => {
  if (Array.isArray(sortValue)) {
    if (sortValue.length === 0) return undefined;
    if (sortValue.length === 1) {
      return typeof sortValue[0] === "string" ? sortValue[0] : undefined;
    }
    return sortValue.filter((entry) => typeof entry === "string" && entry.trim().length > 0);
  }

  if (typeof sortValue === "string" && sortValue.trim().length > 0) {
    return sortValue.trim();
  }

  return undefined;
};

const buildPageableParams = ({ page = 0, size = 10, sort } = {}) => {
  const params = {
    page,
    size,
  };

  const normalizedSort = normalizeSortParam(sort);
  if (normalizedSort) {
    params.sort = normalizedSort;
  }

  return params;
};

export const fetchLists = async ({ subjectId, createdBy, query, page = 0, size = 10, sort } = {}) => {
  const normalizedSubjectId = typeof subjectId === "string" ? subjectId.trim() : subjectId;
  const normalizedCreatedBy = typeof createdBy === "string" ? createdBy.trim() : createdBy;
  const normalizedQuery = typeof query === "string" ? query.trim() : "";

  const params = {
    ...buildPageableParams({ page, size, sort }),
    ...(normalizedSubjectId ? { subjectId: normalizedSubjectId } : {}),
    createdBy: normalizedCreatedBy ?? "",
    q: normalizedQuery,
  };

  const config = await withAuthHeaders({ params });
  const response = await keywordServiceClient.get(buildKeywordServicePath("lists"), config);
  return normalizePagePayload(response.data, normalizeListRecord);
};

export const fetchListById = async (listId) => {
  if (listId == null) {
    throw new Error("listId is required");
  }

  const config = await withAuthHeaders();
  const response = await keywordServiceClient.get(buildKeywordServicePath(`lists/${listId}`), config);
  return normalizeListRecord(response.data);
};

export const createList = async (payload) => {
  if (!payload || typeof payload !== "object") {
    throw new Error("List payload is required");
  }

  // Ensure all required fields are present
  const listPayload = {
    subjectId: payload.subjectId,
    title: payload.title,
    description: payload.description ?? "",
    coverImageUrl: payload.coverImageUrl ?? "",
    visibility: payload.visibility ?? "PUBLIC",
    ...(payload.coverImageMediaId ? { coverImageMediaId: payload.coverImageMediaId } : {}),
  };

  assertListTextIsClean(listPayload);

  const config = await withAuthHeaders();
  const response = await keywordServiceClient.post(buildKeywordServicePath("lists"), listPayload, config);
  const normalizedList = normalizeListRecord(response.data);
  return {
    ...normalizedList,
    coverImageUrl: normalizedList.coverImageUrl || listPayload.coverImageUrl || "",
    coverImageMediaId:
      normalizedList.coverImageMediaId || listPayload.coverImageMediaId || null,
  };
};

export const updateList = async (listId, payload) => {
  if (listId == null) {
    throw new Error("listId is required");
  }

  if (!payload || typeof payload !== "object") {
    throw new Error("List payload is required");
  }

  const sanitizedPayload = { ...payload };
  delete sanitizedPayload.coverMediaId;
  delete sanitizedPayload.coverImageId;

  assertListTextIsClean(sanitizedPayload);

  const config = await withAuthHeaders();
  const response = await keywordServiceClient.put(
    buildKeywordServicePath(`lists/${listId}`),
    sanitizedPayload,
    config
  );
  return normalizeListRecord(response.data);
};

export const deleteList = async (listId, { keepalive = false } = {}) => {
  if (listId == null) {
    throw new Error("listId is required");
  }

  if (
    keepalive &&
    typeof window !== "undefined" &&
    typeof window.fetch === "function"
  ) {
    const token = getToken();
    if (!token) {
      throw new Error("Authentication is required to delete a list.");
    }

    const response = await window.fetch(
      `${KEYWORD_SERVICE_BASE}/${buildKeywordServicePath(
        `lists/${encodeURIComponent(String(listId))}`,
      )}`,
      {
        method: "DELETE",
        headers: { Authorization: token },
        keepalive: true,
      },
    );

    if (!response.ok) {
      throw new Error(`Unable to delete list (status ${response.status}).`);
    }

    return { success: true };
  }

  const config = await withAuthHeaders();
  await keywordServiceClient.delete(buildKeywordServicePath(`lists/${listId}`), config);
  return { success: true };
};

export const toggleListPinned = async (listId) => {
  if (listId == null) {
    throw new Error("listId is required");
  }

  const token = await getToken();
  const response = await axios.put(
    `/ks/api/lists/${listId}/pinned`,
    null,
    {
      headers: {
        ...(token ? { Authorization: token } : {}),
      },
    },
  );

  if (response.status < 200 || response.status >= 300) {
    throw new Error("Unable to update pinned state.");
  }

  return { success: true };
};

export const recordListView = async (listId) => {
  if (listId == null) {
    throw new Error("listId is required");
  }

  const config = await withAuthHeaders();
  await keywordServiceClient.post(buildKeywordServicePath(`lists/${listId}/view`), null, config);
  return { success: true };
};

export const createListShare = async (listId, expiresInDays) => {
  if (listId == null) {
    throw new Error("listId is required");
  }

  const payload =
    Number.isFinite(expiresInDays) && expiresInDays > 0 ? { expiresInDays } : {};

  const config = await withAuthHeaders();
  const response = await keywordServiceClient.post(
    buildKeywordServicePath(`lists/${listId}/share`),
    payload,
    config
  );

  return response.data || {};
};

export const revokeListShare = async (listId) => {
  if (listId == null) {
    throw new Error("listId is required");
  }

  const config = await withAuthHeaders();
  await keywordServiceClient.delete(buildKeywordServicePath(`lists/${listId}/share`), config);
  return { success: true };
};

export const fetchSharedListByToken = async (token) => {
  const normalizedToken = typeof token === "string" ? token.trim() : "";
  if (!normalizedToken) {
    throw new Error("token is required");
  }

  const config = await withAuthHeaders();
  const response = await keywordServiceClient.get(
    buildKeywordServicePath(`lists/shared/${normalizedToken}`),
    config
  );
  return normalizeListRecord(response.data);
};

const normalizeKeywordRecord = (payload) => {
  if (!payload || typeof payload !== "object") {
    return {
      id: null,
      uuid: null,
      term: "",
    };
  }

  const rawId = payload.id || payload.keywordId || payload.uuid || null;
  const keywordId = coerceId(rawId);
  const normalizedDefinition = getKeywordDefinitionText(payload);

  return {
    ...payload,
    id: keywordId,
    uuid: keywordId != null ? String(keywordId) : payload.uuid || null,
    term: payload.term || payload.name || payload.keyword || "",
    definition: normalizedDefinition,
    question: getKeywordQuestionText(payload),
  };
};

const normalizeKeywordPagePayload = (payload) => {
  const pageContent = Array.isArray(payload?.content)
    ? payload.content
    : Array.isArray(payload?.items)
    ? payload.items
    : Array.isArray(payload?.keywords)
    ? payload.keywords
    : Array.isArray(payload?.keyword)
    ? payload.keyword
    : [];

  const keywords = pageContent.map(normalizeKeywordRecord);

  const pageNumber = isFiniteNumber(payload?.pageable?.pageNumber)
    ? payload.pageable.pageNumber
    : isFiniteNumber(payload?.page)
    ? payload.page
    : isFiniteNumber(payload?.pagination?.page)
    ? Math.max(0, payload.pagination.page - 1)
    : 0;

  const pageSize = isFiniteNumber(payload?.pageable?.pageSize)
    ? payload.pageable.pageSize
    : isFiniteNumber(payload?.size)
    ? payload.size
    : isFiniteNumber(payload?.pagination?.size)
    ? payload.pagination.size
    : keywords.length;

  const totalElements = isFiniteNumber(payload?.totalElements)
    ? payload.totalElements
    : isFiniteNumber(payload?.pagination?.total)
    ? payload.pagination.total
    : keywords.length;

  const totalPages = isFiniteNumber(payload?.totalPages)
    ? payload.totalPages
    : isFiniteNumber(payload?.pagination?.totalPages)
    ? payload.pagination.totalPages
    : pageSize > 0
    ? Math.ceil(totalElements / pageSize)
    : 0;

  return {
    keyword: keywords,
    list: keywords,
    pagination: {
      page: pageNumber + 1,
      pageIndex: pageNumber,
      size: pageSize,
      total: totalElements,
      totalPages,
    },
  };
};

export const fetchListKeywords = async (listId, { page = 0, size = 20, sort = [] } = {}) => {
  if (listId == null) {
    throw new Error("listId is required");
  }

  const params = buildPageableParams({ page, size, sort });
  const debugStack = import.meta.env.DEV
    ? new Error("fetchListKeywords caller").stack
    : null;
  const config = await withAuthHeaders({ params });
  let response;

  try {
    response = await keywordServiceClient.get(
      buildKeywordServicePath(`lists/${listId}/keywords`),
      config
    );
  } catch (error) {
    if (import.meta.env.DEV) {
      console.warn("[fetchListKeywords] request failed", {
        listId,
        params,
        status: error?.response?.status,
        response: error?.response?.data,
        caller: debugStack,
      });
    }
    throw error;
  }

  return normalizePagePayload(response.data, normalizeKeywordRecord);
};

export const addKeywordToList = async (listId, payload) => {
  if (listId == null) {
    throw new Error("listId is required");
  }

  if (!payload || typeof payload !== "object") {
    throw new Error("Keyword payload is required");
  }

  assertKeywordPayloadTextIsClean(payload);

  assertKeywordDefinitionIsSafe(
    payload?.name || payload?.keywordName || payload?.keyword,
    getKeywordDefinitionText(payload),
  );

  const config = await withAuthHeaders();
  const response = await keywordServiceClient.post(
    buildKeywordServicePath(`lists/${listId}/keywords`),
    payload,
    config
  );
  return normalizeKeywordRecord(response.data);
};

export const addKeywordsToListBatch = async (
  listId,
  payloads,
  { onProgress, fallbackToSequential = true } = {},
) => {
  if (listId == null) {
    throw new Error("listId is required");
  }

  const keywordPayloads = Array.isArray(payloads)
    ? payloads.filter((item) => item && typeof item === "object")
    : [];

  if (!keywordPayloads.length) {
    throw new Error("At least one keyword payload is required");
  }

  keywordPayloads.forEach((payload) => {
    assertKeywordPayloadTextIsClean(payload);
    assertKeywordDefinitionIsSafe(
      payload?.name || payload?.keywordName || payload?.keyword,
      getKeywordDefinitionText(payload),
    );
  });

  try {
    const config = await withAuthHeaders();
    const response = await keywordServiceClient.post(
      buildKeywordServicePath(`lists/${listId}/keywords/bulk`),
      { keywords: keywordPayloads },
      config,
    );
    const createdKeywords = Array.isArray(response.data)
      ? response.data.map(normalizeKeywordRecord)
      : [];

    onProgress?.({
      current: createdKeywords.length || keywordPayloads.length,
      total: keywordPayloads.length,
      keywords: createdKeywords,
      bulk: true,
    });

    return createdKeywords;
  } catch (error) {
    if (!fallbackToSequential) {
      const message =
        extractApiErrorMessage(error) || "Keyword bulk creation failed.";
      const wrappedError = new Error(message);
      wrappedError.response = error?.response;
      throw wrappedError;
    }

    if (import.meta.env.DEV) {
      console.warn("[addKeywordsToListBatch] bulk create failed; falling back to sequential", {
        listId,
        keywordCount: keywordPayloads.length,
        status: error?.response?.status,
        response: error?.response?.data,
      });
    }
  }

  const createdKeywords = [];

  for (let index = 0; index < keywordPayloads.length; index += 1) {
    try {
      const createdKeyword = await addKeywordToList(listId, keywordPayloads[index]);
      createdKeywords.push(createdKeyword);
      onProgress?.({
        current: index + 1,
        total: keywordPayloads.length,
        keyword: createdKeyword,
      });
    } catch (error) {
      const keywordName = normalizeText(
        keywordPayloads[index]?.name || keywordPayloads[index]?.keywordName,
      );
      const status = error?.response?.status;
      const message =
        extractApiErrorMessage(error) ||
        (status === 409
          ? "A keyword with this name already exists in this list."
          : "Keyword creation failed.");
      const wrappedError = new Error(
        keywordName ? `Unable to save "${keywordName}": ${message}` : message,
      );
      wrappedError.response = error?.response;
      throw wrappedError;
    }
  }

  return createdKeywords;
};

export const generateListKeywordCandidates = async ({
  listId,
  topic,
  subjectName,
  difficulty,
  count = 10,
  distractorCount = 3,
}) => {
  if (listId == null) {
    throw new Error("listId is required");
  }

  const normalizedTopic = normalizeText(topic);
  if (!normalizedTopic) {
    throw new Error("topic is required");
  }

  const config = await withAuthHeaders();

  const response = await keywordServiceClient.post(
    buildKeywordServicePath(`lists/${listId}/keywords/ai-generate`),
    {
      topic: normalizedTopic,
      ...(normalizeText(subjectName)
        ? { subjectName: normalizeText(subjectName) }
        : {}),
      ...(normalizeText(difficulty) ? { difficulty: normalizeText(difficulty) } : {}),
      count: clampInteger(count, {
        min: 1,
        max: MAX_AI_GENERATED_KEYWORD_CANDIDATES,
        fallback: 10,
      }),
      distractorCount: clampInteger(distractorCount, {
        min: 1,
        max: 6,
        fallback: 3,
      }),
    },
    config
  );

  return extractGeneratedKeywordCandidates(response?.data);
};

export const regenerateListKeywordCandidate = async ({
  listId,
  topic,
  subjectName,
  currentName,
  currentContext,
  distractorCount = 3,
}) => {
  if (listId == null) {
    throw new Error("listId is required");
  }

  const normalizedTopic = normalizeText(topic);
  if (!normalizedTopic) {
    throw new Error("topic is required");
  }

  const config = await withAuthHeaders();
  let lastCandidate = null;

  for (let attempt = 0; attempt < AI_DEFINITION_GENERATION_ATTEMPTS; attempt += 1) {
    const response = await keywordServiceClient.post(
      buildKeywordServicePath(`lists/${listId}/keywords/ai-regenerate-one`),
      {
        topic: normalizedTopic,
        ...(normalizeText(subjectName)
          ? { subjectName: normalizeText(subjectName) }
          : {}),
        ...(normalizeText(currentName)
          ? { currentName: normalizeText(currentName) }
          : {}),
        ...(normalizeText(currentContext)
          ? { currentContext: normalizeText(currentContext) }
          : {}),
        distractorCount: clampInteger(distractorCount, {
          min: 1,
          max: 6,
          fallback: 3,
        }),
      },
      config
    );

    const candidate = normalizeGeneratedKeywordCandidate(
      pickGeneratedKeywordCandidatePayload(response?.data),
    );
    lastCandidate = candidate;

    if (isKeywordDefinitionSafe(candidate?.name, candidate?.definition)) {
      return candidate;
    }
  }

  throw buildUnsafeGeneratedDefinitionError(lastCandidate?.name || currentName);
};

export const generateKeywordDefinitions = async ({
  keyword,
  subjectName,
  definitionCount = 1,
  currentDefinition,
  excludeDefinitions = [],
  regenerate = false,
}) => {
  const normalizedKeyword = normalizeText(keyword);
  if (!normalizedKeyword) {
    throw new Error("keyword is required");
  }

  const config = await withAuthHeaders();
  const normalizedCurrentDefinition = normalizeText(currentDefinition);
  const normalizedExcludeDefinitions = normalizeStringList(excludeDefinitions);

  for (let attempt = 0; attempt < AI_DEFINITION_GENERATION_ATTEMPTS; attempt += 1) {
    const response = await keywordServiceClient.post(
      buildKeywordServicePath("ai/keywords/definitions"),
      {
        keyword: normalizedKeyword,
        ...(normalizeText(subjectName)
          ? { subjectName: normalizeText(subjectName) }
          : {}),
        definitionCount: Math.max(1, Number(definitionCount) || 1),
        ...(normalizedCurrentDefinition
          ? { currentDefinition: normalizedCurrentDefinition }
          : {}),
        ...(normalizedExcludeDefinitions.length
          ? { excludeDefinitions: normalizedExcludeDefinitions }
          : {}),
        ...(regenerate ? { regenerate: true } : {}),
      },
      config
    );

    const data = response?.data || {};
    if (isKeywordDefinitionSafe(normalizedKeyword, getKeywordDefinitionText(data))) {
      return data;
    }
  }

  throw buildUnsafeGeneratedDefinitionError(normalizedKeyword);
};

export const generateKeywordQuestions = async ({
  keyword,
  subjectName,
  context,
  definition,
  questionCount = 1,
  distractorCount = 3,
}) => {
  const normalizedKeyword = normalizeText(keyword);
  const normalizedContext = normalizeText(context);
  const normalizedDefinition = normalizeText(definition);

  if (!normalizedKeyword) {
    throw new Error("keyword is required");
  }

  if (!normalizedContext) {
    throw new Error("context is required");
  }

  if (!normalizedDefinition) {
    throw new Error("definition is required");
  }

  const config = await withAuthHeaders();

  for (let attempt = 0; attempt < AI_DEFINITION_GENERATION_ATTEMPTS; attempt += 1) {
    const response = await keywordServiceClient.post(
      buildKeywordServicePath("ai/keywords/questions"),
      {
        keyword: normalizedKeyword,
        ...(normalizeText(subjectName)
          ? { subjectName: normalizeText(subjectName) }
          : {}),
        context: normalizedContext,
        definition: normalizedDefinition,
        questionCount: Math.max(1, Number(questionCount) || 1),
        distractorCount: Math.max(1, Number(distractorCount) || 1),
      },
      config
    );

    const data = response?.data || {};
    if (isKeywordDefinitionSafe(normalizedKeyword, getKeywordDefinitionText(data))) {
      return data;
    }
  }

  throw buildUnsafeGeneratedDefinitionError(normalizedKeyword);
};

export const generateKeywordDistractors = async ({
  question,
  distractorCount = 3,
}) => {
  const normalizedQuestion = normalizeText(question);
  if (!normalizedQuestion) {
    throw new Error("question is required");
  }

  const config = await withAuthHeaders();
  const response = await keywordServiceClient.post(
    buildKeywordServicePath("distractors"),
    {
      question: normalizedQuestion,
      distractorCount: Math.max(1, Number(distractorCount) || 1),
    },
    config
  );

  return response?.data || {};
};

const rankKeywordsFromRankedLists = async ({
  page = 0,
  size = 10,
  listSort,
  keywordSort = "views,DESC",
  rankField,
  listPageSize = 10,
} = {}) => {
  const listsPage = await fetchLists({ page, size: listPageSize, sort: listSort });
  const rankedLists = Array.isArray(listsPage?.list) ? listsPage.list : [];

  if (rankedLists.length === 0) {
    return normalizeKeywordPagePayload({
      keyword: [],
      pagination: listsPage?.pagination,
    });
  }

  const normalizedKeywordSort =
    typeof keywordSort === "string" && keywordSort.trim().length > 0
      ? [keywordSort.trim()]
      : [];

  const listKeywords = await Promise.all(
    rankedLists.map(async (list) => {
      const embeddedKeywords = Array.isArray(list?.keywords)
        ? list.keywords.map(normalizeKeywordRecord).filter((entry) => entry?.id != null)
        : [];

      if (embeddedKeywords.length > 0) {
        return embeddedKeywords;
      }

      const listId = list?.id ?? list?.uuid ?? list?.href;
      if (listId == null || listId === "") {
        return [];
      }

      try {
        const pagePayload = await fetchListKeywords(listId, {
          page: 0,
          size: 100,
          sort: normalizedKeywordSort,
        });
        return Array.isArray(pagePayload?.list) ? pagePayload.list : [];
      } catch {
        const fallbackPayload = await fetchListKeywords(listId, {
          page: 0,
          size: 100,
        });
        return Array.isArray(fallbackPayload?.list) ? fallbackPayload.list : [];
      }
    })
  );

  const mergedKeywords = listKeywords.flat();
  const scoreField =
    typeof rankField === "string" && rankField.trim().length > 0
      ? rankField.trim()
      : String(keywordSort).split(",")[0] || "views";
  const keywordMap = new Map();

  mergedKeywords.forEach((keyword) => {
    const normalizedKeyword = normalizeKeywordRecord(keyword);
    const key = normalizedKeyword?.id != null ? String(normalizedKeyword.id) : normalizedKeyword.term;
    if (!key) return;

    const score = Number(normalizedKeyword?.[scoreField] ?? 0);
    const existing = keywordMap.get(key);
    const existingScore = Number(existing?.[scoreField] ?? 0);

    if (!existing || score > existingScore) {
      keywordMap.set(key, normalizedKeyword);
    }
  });

  const rankedKeywords = Array.from(keywordMap.values())
    .sort((a, b) => {
      const left = Number(a?.[scoreField] ?? 0);
      const right = Number(b?.[scoreField] ?? 0);
      if (right !== left) return right - left;
      const leftTerm = String(a?.term || "").toLowerCase();
      const rightTerm = String(b?.term || "").toLowerCase();
      return leftTerm.localeCompare(rightTerm);
    })
    .slice(0, size);

  return {
    keyword: rankedKeywords,
    list: rankedKeywords,
    pagination: listsPage?.pagination || {
      page: page + 1,
      pageIndex: page,
      size,
      total: rankedKeywords.length,
      totalPages: rankedKeywords.length > 0 ? 1 : 0,
    },
  };
};

export const getMostViewedLists = async ({ page = 0, size = 10 } = {}) => {
  return fetchLists({ page, size, sort: "viewsCount,DESC" });
};

export const getTrendingLists = async ({ page = 0, size = 10 } = {}) => {
  return fetchLists({ page, size, sort: "likesCount,DESC" });
};

export const getMostViewedKeywords = async ({ page = 0, size = 10 } = {}) => {
  return rankKeywordsFromRankedLists({
    page,
    size,
    listSort: "viewsCount,DESC",
    keywordSort: "views,DESC",
  });
};

export const getTrendingKeywords = async ({ page = 0, size = 10 } = {}) => {
  return rankKeywordsFromRankedLists({
    page,
    size,
    listSort: "likesCount,DESC",
    keywordSort: "",
    rankField: "likesCount",
  });
};

export const searchLists = async ({ query, subjectId, page = 0, size = 10, sort } = {}) => {
  return fetchLists({ query, subjectId, page, size, sort });
};

export const fetchUserLists = async ({
  userId,
  subjectId,
  page = 0,
  size = 10,
  sort = ["pinned,DESC", "createdAt,DESC"],
} = {}) => {
  return fetchLists({ createdBy: userId, subjectId, page, size, sort });
};

export const fetchMyLists = async ({
  page = 0,
  size = 100,
  sort = ["pinned,DESC", "createdAt,DESC"],
} = {}) => {
  const params = buildPageableParams({ page, size, sort });
  const config = await withAuthHeaders({ params });
  const response = await keywordServiceClient.get(buildKeywordServicePath("lists/me"), config);
  return normalizePagePayload(response.data, normalizeListRecord);
};

export const fetchSubjectsAutocomplete = async ({
  query = "",
  page = 0,
  size = 20,
  sort = "name",
  signal,
} = {}) => {
  const sortArray = Array.isArray(sort)
    ? sort
    : typeof sort === "string" && sort.trim().length > 0
    ? [sort.trim()]
    : [];

  const params = {
    page,
    size,
    ...(sortArray.length > 0 ? { sort: sortArray } : {}),
    ...(query ? { name: query } : {}),
  };

  const config = await withAuthHeaders({ params, signal });
  const response = await keywordServiceClient.get(
    buildKeywordServicePath("subjects"),
    config
  );
  return normalizeSubjectPagePayload(response.data);
};
